import * as XLSX from "xlsx";
import { computeDelta, computeValue } from "./compute";
import { SHOW_BENCHMARK_LABEL } from "./constants";
import { formatDateRange } from "./dates";
import { fmtVal, formatAbsDelta } from "./format";
import type { DateRange, Delta, Metric, Registry, Section } from "./types";

/** The Benchmark cell. Held at "—" while the benchmark labels are hidden, so the
 *  workbook matches what the screens show; flipping SHOW_BENCHMARK_LABEL back on
 *  restores the real target in both places at once. */
const benchCell = (m: Metric): string =>
  SHOW_BENCHMARK_LABEL ? m.benchLabel || "—" : "—";

/** Excel rejects []:*?/\ in sheet names and caps them at 31 chars. */
const sheetName = (title: string): string =>
  title.replace(/[·:/\\?*[\]]/g, " ").replace(/\s+/g, " ").trim().slice(0, 28) || "Export";

/** Sheet title → a safe file-name fragment ("Sales & Funnel" → "Sales_Funnel"). */
const fileFragment = (title: string): string =>
  title.replace(/[^\w]+/g, "_").replace(/^_+|_+$/g, "") || "Export";

/** Export the current (role-filtered) sections + snapshot to a workbook. Used as
 *  the fallback on screens that carry no live dashboard (Guide / All Metrics /
 *  Current State) — see `exportDashboardXlsx` for the active pillar tab. */
export function exportXlsx(reg: Registry, sections: Section[], snap: Section, range: DateRange): void {
  const wb = XLSX.utils.book_new();
  const allRows: (string | number)[][] = [["Metric", "Value", "Benchmark", "Δ vs prior"]];
  [...sections, snap].forEach((sec) => {
    const rows: (string | number)[][] = [[sec.title], ["Metric", "Value", "Benchmark", "Δ vs prior"]];
    sec.ids.forEach((id) => {
      const m = reg[id]; const v = computeValue(m, range); const d = computeDelta(m, range);
      rows.push([m.name, fmtVal(m, v), benchCell(m), formatAbsDelta(m, d)]);
      allRows.push([m.name, fmtVal(m, v), benchCell(m), formatAbsDelta(m, d)]);
    });
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, sheetName(sec.title));
  });
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(allRows), "All Metrics");
  XLSX.writeFile(wb, "DayLight_Export.xlsx");
}

/**
 * Export an already-shaped grid as a one-sheet workbook: a title, an optional
 * context line (date range / period span), then a header row and the body. Used
 * by the All Metrics views, whose tabs are wide grids rather than metric lists —
 * a row with a single cell renders as a group heading, mirroring the screen.
 */
export function exportGridXlsx(
  title: string,
  header: string[],
  rows: (string | number)[][],
  subtitle?: string,
): void {
  const wb = XLSX.utils.book_new();
  const sheet: (string | number)[][] = [[title]];
  if (subtitle) sheet.push([subtitle]);
  sheet.push([], header, ...rows);
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sheet), sheetName(title));
  XLSX.writeFile(wb, `DayLight_${fileFragment(title)}.xlsx`);
}

/** One exported metric: the adapted `Metric` (for units/formatting) plus its live
 *  current and prior values. Callers in the component layer build these from the
 *  API response — this module stays free of API types. */
export interface DashboardExportRow {
  metric: Metric;
  value: number | null;
  /** Period-over-period delta, or null when there is no prior value to compare
   *  against (an em dash beats a fabricated 0%). */
  delta: Delta | null;
}

/** The detail table on screen (By Branch / By Clinician), already flattened to
 *  display strings: a worksheet title, its header row, and its body rows in the
 *  order shown — search, sort and column choices applied. */
export interface DetailTableSheet {
  title: string;
  header: string[];
  rows: (string | number)[][];
}

/**
 * Export the dashboard (pillar tab) currently on screen: one sheet holding
 * exactly the metrics that tab is showing, for the selected date range and
 * filters. Values are the live API numbers, not the seeded series.
 *
 * `tables` adds each detail table the dashboard renders — By Branch, By
 * Clinician, … in on-screen order — as a further worksheet in the same file.
 */
export function exportDashboardXlsx(
  title: string,
  rows: DashboardExportRow[],
  range: DateRange,
  tables: DetailTableSheet[] = [],
): void {
  const wb = XLSX.utils.book_new();
  const sheet: (string | number)[][] = [
    [title],
    [formatDateRange(range)],
    [],
    ["Metric", "Value", "Benchmark", "Δ vs prior"],
  ];
  rows.forEach(({ metric, value, delta }) => {
    sheet.push([
      metric.name,
      value === null ? "—" : fmtVal(metric, value),
      benchCell(metric),
      delta === null ? "—" : formatAbsDelta(metric, delta),
    ]);
  });
  // Workbook sheet names must be unique (XLSX throws otherwise), and two tables
  // may share a title — or share one with the dashboard.
  const used = new Set<string>();
  const uniqueSheetName = (t: string): string => {
    const base = sheetName(t);
    let name = base;
    for (let n = 2; used.has(name.toLowerCase()); n++) name = `${base.slice(0, 24)} (${n})`;
    used.add(name.toLowerCase());
    return name;
  };
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sheet), uniqueSheetName(title));
  // A sheet only for a table actually on screen with rows in it — an empty
  // worksheet would read as "no data" rather than "no table here".
  for (const table of tables) {
    if (!table.rows.length) continue;
    const tbl: (string | number)[][] = [
      [table.title],
      [formatDateRange(range)],
      [],
      table.header,
      ...table.rows,
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(tbl), uniqueSheetName(table.title));
  }
  XLSX.writeFile(wb, `DayLight_${fileFragment(title)}.xlsx`);
}
